import { useEffect, useRef } from 'react';
import { ScrollText } from 'lucide-react';
import { Badge, type BadgeTone } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useClusterDeploymentLogs, type K8sDeploymentLog } from '@/api/k8s';

// Map a log level to a Badge tone (mirrors @console's coloured level chips).
const levelTone = (level?: string): BadgeTone => {
  const v = (level ?? '').toLowerCase();
  if (/err|fail|fatal/.test(v)) return 'danger';
  if (/warn/.test(v)) return 'warning';
  if (/success|done|complete|ready/.test(v)) return 'success';
  if (/debug/.test(v)) return 'accent';
  return 'info';
};

const fmtTs = (ts?: string) => {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString();
};

/**
 * Real-time deployment-progress stream — @console's "Deployment Progress" block,
 * rendered INSIDE the provisioning info Alert so it shares that styling. Reads the
 * structured `metadata.deployment_logs` entries (polled 5s) and tails them: the view
 * auto-scrolls to the newest line so it reads as a continuous live stream. Renders
 * nothing until there's activity, so the Alert's own copy stands alone at first.
 */
export function ProvisioningLogs({
  serviceId,
  clusterId,
  alwaysShow = false,
}: {
  serviceId: string;
  clusterId: string;
  /** When true (Logs tab) the stream stays mounted even with no entries — shows an
   *  empty state instead of collapsing — and drops the in-Alert top divider. Default
   *  (Overview) hides until there's activity. */
  alwaysShow?: boolean;
}) {
  const { data } = useClusterDeploymentLogs(serviceId, clusterId);
  const logs: K8sDeploymentLog[] = data?.logs ?? [];
  // Backend sends last_error as a plain string OR a structured entry
  // ({stage,message,timestamp}); coerce to text so it never renders as an object.
  const rawError = data?.last_error;
  const lastError = typeof rawError === 'string' ? rawError : rawError?.message ?? '';
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the view is "stuck" to the newest line. Starts pinned; a user scrolling
  // up releases the pin so the next poll doesn't yank them back down.
  const pinnedRef = useRef(true);
  const onScroll = () => {
    const el = scrollRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  // Tail-follow: pin to the newest line whenever the log grows, unless the user
  // has scrolled up to read earlier lines.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [logs.length]);

  if (logs.length === 0 && !lastError) {
    if (!alwaysShow) return null;
    return (
      <EmptyState icon={<ScrollText size={20} />} title="No activity yet" description="Deployment logs appear as provisioning progresses." />
    );
  }

  return (
    <div
      style={
        alwaysShow
          ? undefined
          : { marginTop: 'var(--vn-space-3)', borderTop: '1px solid var(--vn-info-border)', paddingTop: 'var(--vn-space-2)' }
      }
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        style={{
          maxHeight: alwaysShow ? 360 : 200,
          overflowY: 'auto',
          display: 'grid',
          gap: 'var(--vn-space-1)',
          fontFamily: 'var(--vn-font-mono)',
          fontSize: 'var(--vn-font-size-xs)',
          lineHeight: 1.6,
          // In the Logs tab, render on a code surface (like @console's terminal block).
          ...(alwaysShow
            ? {
                background: 'var(--vn-bg-subtle)',
                border: '1px solid var(--vn-border-default)',
                borderRadius: 'var(--vn-radius-md)',
                padding: 'var(--vn-space-3)',
              }
            : {}),
        }}
      >
        {logs.map((log, i) => (
          <div key={`${log.timestamp ?? ''}-${i}`} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--vn-fg-muted)', minWidth: 72, flexShrink: 0 }}>{fmtTs(log.timestamp)}</span>
            {log.stage && <Badge tone="info" appearance="subtle" size="sm">{log.stage}</Badge>}
            {log.level && <Badge tone={levelTone(log.level)} appearance="subtle" size="sm">{log.level}</Badge>}
            <span style={{ color: 'var(--vn-fg-default)', wordBreak: 'break-word' }}>{log.message}</span>
          </div>
        ))}
      </div>
      {lastError && (
        <div style={{ marginTop: 'var(--vn-space-2)', color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
          Error: {lastError}
        </div>
      )}
    </div>
  );
}
